import React, { useCallback, useEffect, useRef, useState } from 'react';

import { VolumeSlider } from '../../../components/VolumeSlider';
import { useAuth } from '../../../AuthProvider';
import {
    applyAudioSettingsPatch,
    normalizeAudioSettingLevel0to100,
} from '../../../utils/applyAudioSettingsPatch';
import { useGameStateContext } from '../../context/useGameStateContext';
import useGameSocket from '../../hooks/useGameSocket';
import type { GamePhase } from '../../types/game.types';

import './GameSettings.css';

const PHASES_ALLOWING_CONCEDE: readonly GamePhase[] = [
    'ISLAND_PLACEMENT',
    'MONSTER_PLACEMENT',
    'ACTIVE',
];

/** Delay after last slider movement before persisting to the server (registered users). */
const VOLUME_PATCH_DEBOUNCE_MS = 400;

export interface GameSettingsProps {
    open: boolean;
    onClose: () => void;
}

/**
 * In-game settings modal (volume sliders, concede). Overlay closes on backdrop click.
 * Music / SFX: immediate local update for audio; `patchUserSettings` (network) runs after the
 * slider has been still for {@link VOLUME_PATCH_DEBOUNCE_MS}, and pending saves flush when the modal closes.
 */
export const GameSettings: React.FC<GameSettingsProps> = ({ open, onClose }) => {
    const { user, storeUser, patchUserSettings } = useAuth();
    const { phase } = useGameStateContext();
    const { concede, isConnected } = useGameSocket();
    const [conceding, setConceding] = useState(false);

    const userRef = useRef(user);
    userRef.current = user;

    const musicTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sfxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingMusicNetworkRef = useRef(0);
    const pendingSfxNetworkRef = useRef(0);
    const patchUserSettingsRef = useRef(patchUserSettings);
    patchUserSettingsRef.current = patchUserSettings;

    const flushPendingVolumePatches = useCallback(() => {
        if (musicTimerRef.current) {
            clearTimeout(musicTimerRef.current);
            musicTimerRef.current = null;
            void patchUserSettingsRef.current({ music: pendingMusicNetworkRef.current }).catch(() => {
                alert('Could not save music setting. Please try again.');
            });
        }
        if (sfxTimerRef.current) {
            clearTimeout(sfxTimerRef.current);
            sfxTimerRef.current = null;
            void patchUserSettingsRef.current({ sfx: pendingSfxNetworkRef.current }).catch(() => {
                alert('Could not save sound effects setting. Please try again.');
            });
        }
    }, []);

    const canConcede = PHASES_ALLOWING_CONCEDE.includes(phase) && isConnected;

    const musicLevel = normalizeAudioSettingLevel0to100(user.settings?.music);
    const sfxLevel = normalizeAudioSettingLevel0to100(user.settings?.sfx);

    const applyLocalAudioPatch = useCallback((patch: { music?: number; sfx?: number }) => {
        const u = userRef.current;
        storeUser({
            settings: applyAudioSettingsPatch(
                { userId: u.userId, guest: u.guest, settings: u.settings },
                patch
            ),
        });
    }, [storeUser]);

    const scheduleMusicNetworkPatch = useCallback(
        (next: number) => {
            const n = normalizeAudioSettingLevel0to100(next);
            pendingMusicNetworkRef.current = n;
            applyLocalAudioPatch({ music: n });
            if (musicTimerRef.current) {
                clearTimeout(musicTimerRef.current);
            }
            musicTimerRef.current = setTimeout(() => {
                musicTimerRef.current = null;
                void patchUserSettingsRef.current({ music: pendingMusicNetworkRef.current }).catch(() => {
                    alert('Could not save music setting. Please try again.');
                });
            }, VOLUME_PATCH_DEBOUNCE_MS);
        },
        [applyLocalAudioPatch]
    );

    const scheduleSfxNetworkPatch = useCallback(
        (next: number) => {
            const n = normalizeAudioSettingLevel0to100(next);
            pendingSfxNetworkRef.current = n;
            applyLocalAudioPatch({ sfx: n });
            if (sfxTimerRef.current) {
                clearTimeout(sfxTimerRef.current);
            }
            sfxTimerRef.current = setTimeout(() => {
                sfxTimerRef.current = null;
                void patchUserSettingsRef.current({ sfx: pendingSfxNetworkRef.current }).catch(() => {
                    alert('Could not save sound effects setting. Please try again.');
                });
            }, VOLUME_PATCH_DEBOUNCE_MS);
        },
        [applyLocalAudioPatch]
    );

    useEffect(() => {
        if (!open) {
            flushPendingVolumePatches();
        }
    }, [open, flushPendingVolumePatches]);

    useEffect(
        () => () => {
            flushPendingVolumePatches();
        },
        [flushPendingVolumePatches]
    );

    const handleConcede = useCallback(() => {
        if (!canConcede || conceding) return;
        setConceding(true);
        concede((response) => {
            setConceding(false);
            if (response.success) {
                onClose();
                return;
            }
            alert(response.error ?? 'Could not concede. Please try again.');
        });
    }, [canConcede, conceding, concede, onClose]);

    if (!open) return null;

    return (
        <div className="game_settings_overlay" role="presentation" onClick={onClose}>
            <div
                className="game_settings_panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="game-settings-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="game-settings-title" className="game_settings_title">
                    Settings
                </h2>

                <div className="game_settings_options">
                    <div className="game_settings_volume_row">
                        <label className="game_settings_field_label" htmlFor="game-settings-music">
                            Music
                        </label>
                        <VolumeSlider
                            id="game-settings-music"
                            value={musicLevel}
                            onChange={scheduleMusicNetworkPatch}
                        />
                    </div>
                    <div className="game_settings_volume_row">
                        <label className="game_settings_field_label" htmlFor="game-settings-sfx">
                            Sound FX
                        </label>
                        <VolumeSlider
                            id="game-settings-sfx"
                            value={sfxLevel}
                            onChange={scheduleSfxNetworkPatch}
                        />
                    </div>
                </div>

                <div className="game_settings_footer">
                    <button
                        type="button"
                        className="game_settings_concede"
                        disabled={!canConcede || conceding}
                        title={
                            !isConnected
                                ? 'Not connected'
                                : phase === 'LOBBY'
                                  ? 'Not available in the lobby'
                                  : phase === 'ENDED'
                                    ? 'Game has ended'
                                    : !PHASES_ALLOWING_CONCEDE.includes(phase)
                                      ? 'Cannot concede in this phase'
                                      : undefined
                        }
                        onClick={handleConcede}
                    >
                        {conceding ? 'Conceding…' : 'Concede'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GameSettings;
